import React, { useState, useEffect, useLayoutEffect, useRef } from 'react'
import PropTypes from 'prop-types'
import ReactHtmlParser from 'react-html-parser'
import Text from '../../common/Text'
import { motion, AnimatePresence } from 'framer-motion'
import styled, { css } from 'styled-components'
import { rem, transparentize } from 'polished'
import { get } from 'lodash'
import Check from '../../common/Check'

const RewardsAccordian = ({ items, ...data }) => {
  const [activeIndex, setIndex] = useState(null)

  const title = get(data, 'matrix_title.text')
  const description = get(data, 'label_description')

  const cards = [
    {
      image: get(data, 'column_1_image.url'),
      label: get(data, 'column_1_label'),
    },
    {
      image: get(data, 'column_2_image.url'),
      label: get(data, 'column_2_label'),
    },
    {
      image: get(data, 'column_3_image.url'),
      label: get(data, 'column_3_label'),
    },
  ]

  // Mobile detection matches CSS breakpoint
  const [isMobile, setIsMobile] = useState(false)
  useEffect(() => {
    const mq = window.matchMedia('(max-width: 768px)')
    const onChange = () => setIsMobile(mq.matches)
    onChange()
    mq.addEventListener
      ? mq.addEventListener('change', onChange)
      : mq.addListener(onChange)
    return () =>
      mq.removeEventListener
        ? mq.removeEventListener('change', onChange)
        : mq.removeListener(onChange)
  }, [])

  // Sticky offset for the cluster (title height)
  const headerRef = useRef(null)
  const [stickyOffset, setStickyOffset] = useState(0)
  useLayoutEffect(() => {
    if (!headerRef.current) return
    const measure = () => setStickyOffset(0) //setStickyOffset(headerRef.current.getBoundingClientRect().height || 0)
    measure()
    let ro
    if (typeof ResizeObserver !== 'undefined') {
      ro = new ResizeObserver(measure)
      ro.observe(headerRef.current)
    } else {
      window.addEventListener('resize', measure)
    }
    return () => {
      if (ro) ro.disconnect()
      else window.removeEventListener('resize', measure)
    }
  }, [])

  // Precompute row data for mobile table rendering
  const rows =
    items?.map(item => ({
      bodyHtml: get(item, 'row_body.html'),
      col1: get(item, 'column_1') === 'on',
      col2: get(item, 'column_2') === 'on',
      col3: get(item, 'column_3') === 'on',
    })) ?? []

  return (
    <Wrapper style={{ '--sticky-offset': `${stickyOffset}px` }}>
      {/* Sticky header cluster (title + 3 cards) */}
      <HeaderCluster ref={headerRef} style={{ backgroundColor: 'white' }}>
        {title && <ClusterTitle>{title}</ClusterTitle>}

        <ClusterCards>
          {cards.map(({ image, label }, i) => (
            <ClusterCard key={`cluster-card-${i}`}>
              {image && <img src={image} alt={label || ''} width="100" />}
              {label && <span className="label">{label}</span>}
              {description && <span className="sub">{description}</span>}
            </ClusterCard>
          ))}
        </ClusterCards>
      </HeaderCluster>

      {/* MOBILE: table-like rows with checks aligned under the 3 cards (colored + zebra striping) */}
      {isMobile ? (
        <MobileGrid>
          {rows.map((r, idx) => (
            <>
              {/*Make first mobileRow span full width, no columns*/}
              <MobileRow style={{ gridTemplateColumns: '3fr' }}>
                <MobileRowText size="xxs" lineHeight={1.7}>
                  {ReactHtmlParser(r.bodyHtml)}
                </MobileRowText>
              </MobileRow>
              <MobileRow key={`mr-${idx}`} odd={false}>
                {/* Gold column (yellow) */}
                <MobileCell col={1}>
                  {r.col1 ? <Check col={1} color="yellow" /> : null}
                </MobileCell>

                {/* Platinum column (blue) */}
                <MobileCell col={2}>
                  {r.col2 ? <Check col={2} color="accentBlue" /> : null}
                </MobileCell>

                {/* Black column (black) */}
                <MobileCell col={3}>
                  {r.col3 ? <Check col={3} /> : null}
                </MobileCell>
              </MobileRow>
            </>
          ))}
        </MobileGrid>
      ) : (
        // NON-MOBILE: original accordion per-card
        cards.map(({ label, image }, col) => {
          const itemOpen = activeIndex === col
          const handleClick = () => setIndex(itemOpen ? null : col)

          return (
            <Card
              key={`c${col}`}
              first={col === 0}
              onClick={handleClick}
              role="button"
              tabIndex={0}
              aria-expanded={itemOpen}
            >
              <CardHeader>
                <CardText>
                  {image && <img src={image} alt={label || ''} />}
                  <div>
                    {label && <Text.h4>{label}</Text.h4>}
                    {description && (
                      <Text.h6 css={{ opacity: 0.75 }}>{description}</Text.h6>
                    )}
                  </div>
                </CardText>
                {/* PlusMinus intentionally removed */}
              </CardHeader>

              <AnimatePresence initial={false}>
                {itemOpen && (
                  <Body
                    key="content"
                    initial="collapsed"
                    animate="open"
                    exit="collapsed"
                    variants={{
                      open: { opacity: 1, height: 'auto' },
                      collapsed: { opacity: 0, height: 0 },
                    }}
                    transition={{
                      duration: 0.4,
                      ease: [0.04, 0.62, 0.23, 0.98],
                    }}
                  >
                    {items &&
                      items.map((item, row) => {
                        const body = get(item, 'row_body.html')
                        const showThisCol =
                          get(item, `column_${col + 1}`) === 'on'
                        return showThisCol ? (
                          <Text key={`r${row}`} size="xxs" lineHeight={1.7}>
                            {ReactHtmlParser(body)}
                          </Text>
                        ) : null
                      })}
                  </Body>
                )}
              </AnimatePresence>
            </Card>
          )
        })
      )}
    </Wrapper>
  )
}

RewardsAccordian.propTypes = {
  primary: PropTypes.object,
  items: PropTypes.array,
}

export default RewardsAccordian

// =================== styles ===================

const mobile = '@media (max-width: 768px)'

const Wrapper = styled.div`
  --sticky-offset: 0px; /* measured header height */
  position: relative;
`

/** Sticky title + cards cluster */
const HeaderCluster = styled.div`
  ${({ theme }) => css`
    margin-bottom: ${theme.space(3)};
    ${mobile} {
      position: sticky;
      top: var(--sticky-offset, 0px);
      z-index: 20;
      background: ${theme.color.white};
      padding-top: ${theme.space(2)};
      padding-bottom: ${theme.space(2)};
      border-bottom: 1px solid
        ${transparentize(0.5, theme.color.secondaryLightBlue)};
      box-shadow: 0 1px 0 ${transparentize(0.6, theme.color.secondaryLightBlue)};
      margin-bottom: 0;
    }
  `}
`

const ClusterTitle = styled(Text.h2)`
  ${({ theme }) => css`
    text-align: center;
    margin-bottom: ${theme.space(2)};
    ${mobile} {
      margin-bottom: ${theme.space(1)};
    }
  `}
`

/** Cards row inside sticky cluster */
const ClusterCards = styled.div`
  ${({ theme }) => css`
    display: flex;
    justify-content: center;
    gap: ${theme.space(2)};

    ${mobile} {
      display: grid;
      grid-template-columns: repeat(3, 1fr);
      gap: ${theme.space(1)};
      text-align: center;
    }
  `}
`

const ClusterCard = styled.div`
  ${({ theme }) => css`
    display: flex;
    flex-direction: column;
    align-items: center;
    text-align: center;

    img {
      //width: ${rem(44)};
      height: auto;
      object-fit: contain;
      display: block;
    }

    .label {
      font-weight: 800;
      font-size: ${rem(12)};
      line-height: 1.1;
      text-transform: uppercase;
      letter-spacing: 0.02em;
      display: block;
      margin-top: ${theme.space(0.5)};
    }

    .sub {
      font-size: ${rem(10)};
      line-height: 1.1;
      opacity: 0.8;
      display: block;
    }
  `}
`

/** ======= MOBILE TABLE (rows with 3 check columns, zebra striping) ======= */
const MobileGrid = styled.div`
  ${mobile} {
    display: grid;
    grid-auto-rows: auto;
    row-gap: 0; /* borders handle separation */
  }
`

const MobileRow = styled.div`
  ${mobile} {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    align-items: center;
    justify-content: center;
    gap: 12px;
    background-color: ${({ odd, theme }) =>
      odd ? theme.color.softBlue : 'transparent'};
    border-bottom: 1px solid
      ${({ theme }) => transparentize(0.8, theme.color.secondaryLightBlue)};
  }
`

const MobileRowText = styled(Text)`
  ${mobile} {
    padding: ${rem(13.5)} ${({ theme }) => theme.space(1)};
  }
`

const MobileCell = styled.div`
  ${mobile} {
    display: flex;
    align-items: center;
    justify-content: center;
    background-color: #eee;
    padding: ${rem(13.5)} ${({ theme }) => theme.space(1)};
    height: 75px;
  }
`

const CheckIcon = styled.span`
  ${mobile} {
    font-weight: 800;
    line-height: 1;

    /* Column-based colors with sensible fallbacks */
    color: ${({ col, theme }) =>
      col === 1
        ? theme.color.gold || theme.color.warning || '#F7B500' /* Gold */
        : col === 2
        ? theme.color.blue || theme.color.primary || '#1E90FF' /* Platinum */
        : theme.color.black || '#000000'}; /* Black */
  }
`

/** ======= NON-MOBILE ACCORDION (original) ======= */
const Card = styled.div`
  ${({ first, theme }) => css`
    padding-top: ${theme.space(2)};
    padding-bottom: ${theme.space(1)};
    position: relative;
    cursor: pointer;

    ${first &&
      css`
        border-top: 1px solid
          ${transparentize(0.5, theme.color.secondaryLightBlue)};
      `};

    border-bottom: 1px solid
      ${transparentize(0.5, theme.color.secondaryLightBlue)};

    img {
      width: 100%;
      height: auto;
      object-fit: scale-down;
      max-width: ${rem(75)};
    }
  `}
`

const CardHeader = styled.div`
  ${({ theme }) => css`
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding-bottom: ${theme.space(1)};
  `}
`

const CardText = styled.div`
  ${({ theme }) => css`
    display: flex;
    align-items: center;

    > div {
      padding: 0 ${theme.space(1.5)};
    }
  `}
`

const Body = styled(motion.div)`
  ${({ theme }) => css`
    > div,
    > * {
      padding: ${rem(13.5)} ${theme.space(1)};

      &:nth-child(odd) {
        background-color: ${theme.color.softBlue};
      }
    }
  `}
`
